import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Trash2, Layers, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { segmentsApi, leadsApi } from "@/lib/leadsApi";
import { useI18n } from "@/lib/i18n";

const FIELDS = ["industry", "company_size", "country", "seniority", "funding_stage", "status", "score"];
const OPERATORS = { text: ["equals", "not_equals", "contains", "in"], num: ["gte", "lte", "equals"] };

export default function Segments() {
    const { t } = useI18n();
    const [segments, setSegments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [facets, setFacets] = useState({});

    useEffect(() => {
        loadSegments();
        leadsApi.facets().then(setFacets).catch(() => {});
    }, []);

    function loadSegments() {
        setLoading(true);
        segmentsApi.list().then(setSegments).catch(() => toast.error("Failed to load")).finally(() => setLoading(false));
    }

    async function handleDelete(id) {
        if (!window.confirm("Delete this segment?")) return;
        try {
            await segmentsApi.remove(id);
            toast.success("Segment deleted");
            loadSegments();
        } catch (_e) { toast.error("Failed"); }
    }

    return (
        <div className="px-6 lg:px-10 py-6">
            <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
                <div>
                    <h1 className="font-display font-black text-3xl tracking-tighter">{t.find.segments.title}</h1>
                    <p className="mt-1 text-sm text-zinc-500">{t.find.segments.subtitle}</p>
                </div>
                <button
                    type="button"
                    onClick={() => setShowForm(true)}
                    data-testid="segments-create-btn"
                    className="btn-cta text-white font-semibold text-sm px-4 py-2 rounded-full inline-flex items-center gap-2"
                >
                    <Plus size={14} /> {t.find.segments.create}
                </button>
            </div>

            {showForm && (
                <SegmentForm
                    facets={facets}
                    onClose={() => setShowForm(false)}
                    onSaved={() => { setShowForm(false); loadSegments(); }}
                />
            )}

            {loading ? (
                <div className="py-24 flex items-center justify-center text-zinc-500 text-sm">
                    <Loader2 size={18} className="animate-spin mr-2" /> Loading…
                </div>
            ) : segments.length === 0 && !showForm ? (
                <div className="rounded-2xl p-10 text-center bg-brand-surface border border-white/8">
                    <div className="mx-auto w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-indigo to-brand-violet flex items-center justify-center mb-3">
                        <Layers size={22} className="text-white" />
                    </div>
                    <h3 className="font-display font-bold text-xl">{t.find.segments.empty}</h3>
                    <p className="mt-1 text-sm text-zinc-500 max-w-sm mx-auto">{t.find.segments.emptyBody}</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {segments.map((s) => (
                        <motion.div
                            key={s.segment_id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="rounded-2xl p-5 bg-brand-surface border border-white/8 hover:border-white/20 transition group"
                            data-testid={`segment-card-${s.segment_id}`}
                        >
                            <div className="flex items-start justify-between">
                                <div className="min-w-0">
                                    <div className="text-white font-semibold truncate">{s.name}</div>
                                    <div className="mt-1 font-mono text-xs text-brand-cyan">{t.find.segments.leads(s.lead_count)}</div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => handleDelete(s.segment_id)}
                                    data-testid={`segment-delete-${s.segment_id}`}
                                    className="opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-rose-300 transition"
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                            <div className="mt-4 space-y-1.5">
                                {(s.rules?.conditions || []).slice(0, 3).map((c, i) => (
                                    <div key={i} className="text-xs text-zinc-400 truncate font-mono">
                                        <span className="text-zinc-500">{c.field}</span> <span className="text-brand-cyan">{c.operator}</span> <span className="text-white">{Array.isArray(c.value) ? c.value.join(", ") : String(c.value)}</span>
                                    </div>
                                ))}
                                {(s.rules?.conditions?.length || 0) > 3 && (
                                    <div className="text-xs text-zinc-500">+ {s.rules.conditions.length - 3} more</div>
                                )}
                            </div>
                            <Link
                                to="/app/find/leads"
                                className="mt-4 inline-block text-xs text-brand-cyan hover:text-white transition"
                            >
                                View leads →
                            </Link>
                        </motion.div>
                    ))}
                </div>
            )}
        </div>
    );
}

function SegmentForm({ facets, onClose, onSaved }) {
    const { t } = useI18n();
    const [name, setName] = useState("");
    const [combinator, setCombinator] = useState("AND");
    const [conditions, setConditions] = useState([{ field: "industry", operator: "in", value: [] }]);
    const [saving, setSaving] = useState(false);

    function addCondition() {
        setConditions([...conditions, { field: "industry", operator: "in", value: [] }]);
    }
    function removeCondition(i) {
        setConditions(conditions.filter((_, idx) => idx !== i));
    }
    function setCondition(i, patch) {
        setConditions(conditions.map((c, idx) => idx === i ? { ...c, ...patch } : c));
    }

    async function save() {
        if (!name.trim()) { toast.error("Name required"); return; }
        const cleaned = conditions.filter((c) => {
            if (Array.isArray(c.value)) return c.value.length > 0;
            return c.value !== "" && c.value != null;
        });
        if (!cleaned.length) { toast.error("Add at least one condition"); return; }
        setSaving(true);
        try {
            await segmentsApi.create({ name: name.trim(), rules: { combinator, conditions: cleaned } });
            toast.success("Segment created");
            onSaved();
        } catch (_e) { toast.error("Save failed"); }
        finally { setSaving(false); }
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="segment-form"
            className="mb-6 rounded-2xl p-6 bg-brand-surface border border-white/10"
        >
            <div className="flex items-center justify-between mb-4">
                <div className="font-display font-bold text-lg">{t.find.segments.create}</div>
                <button type="button" onClick={onClose} className="text-zinc-500 hover:text-white"><X size={16} /></button>
            </div>

            <div className="mb-4">
                <label className="text-xs text-zinc-400 font-medium">{t.find.segments.nameLabel}</label>
                <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t.find.segments.namePh}
                    data-testid="segment-name-input"
                    className="mt-1 w-full px-3 py-2 rounded-lg bg-brand-void border border-white/10 text-sm outline-none focus:border-brand-indigo"
                />
            </div>

            <div className="mb-3 flex items-center gap-2">
                <span className="text-xs text-zinc-400">{t.find.segments.combinator}</span>
                <button
                    type="button"
                    onClick={() => setCombinator("AND")}
                    data-testid="segment-combinator-and"
                    className={`px-2.5 py-1 rounded text-xs font-mono ${combinator === "AND" ? "bg-brand-indigo text-white" : "bg-white/5 text-zinc-400"}`}
                >{t.find.segments.and}</button>
                <button
                    type="button"
                    onClick={() => setCombinator("OR")}
                    data-testid="segment-combinator-or"
                    className={`px-2.5 py-1 rounded text-xs font-mono ${combinator === "OR" ? "bg-brand-indigo text-white" : "bg-white/5 text-zinc-400"}`}
                >{t.find.segments.or}</button>
            </div>

            <div className="space-y-2">
                {conditions.map((c, i) => {
                    const isNum = c.field === "score";
                    const ops = isNum ? OPERATORS.num : OPERATORS.text;
                    const opts = c.field === "industry" ? facets.industries
                        : c.field === "company_size" ? facets.sizes
                        : c.field === "country" ? facets.countries
                        : c.field === "seniority" ? facets.seniorities
                        : c.field === "funding_stage" ? facets.funding_stages
                        : c.field === "status" ? facets.statuses
                        : null;
                    return (
                        <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`segment-condition-${i}`}>
                            <select
                                value={c.field}
                                onChange={(e) => setCondition(i, { field: e.target.value, operator: e.target.value === "score" ? "gte" : "in", value: e.target.value === "score" ? 70 : [] })}
                                data-testid={`segment-cond-field-${i}`}
                                className="col-span-3 px-2 py-2 rounded-lg bg-brand-void border border-white/10 text-sm outline-none focus:border-brand-indigo"
                            >
                                {FIELDS.map((f) => <option key={f} value={f}>{t.find.segments.fields[f] || f}</option>)}
                            </select>
                            <select
                                value={c.operator}
                                onChange={(e) => setCondition(i, { operator: e.target.value })}
                                data-testid={`segment-cond-op-${i}`}
                                className="col-span-3 px-2 py-2 rounded-lg bg-brand-void border border-white/10 text-sm outline-none focus:border-brand-indigo"
                            >
                                {ops.map((op) => <option key={op} value={op}>{t.find.segments.operators[op] || op}</option>)}
                            </select>
                            <div className="col-span-5">
                                {isNum ? (
                                    <input
                                        type="number"
                                        min={0} max={100}
                                        value={c.value ?? 0}
                                        onChange={(e) => setCondition(i, { value: parseInt(e.target.value, 10) || 0 })}
                                        data-testid={`segment-cond-value-${i}`}
                                        className="w-full px-3 py-2 rounded-lg bg-brand-void border border-white/10 text-sm outline-none focus:border-brand-indigo"
                                    />
                                ) : opts && c.operator === "in" ? (
                                    <div className="flex flex-wrap gap-1 min-h-[36px] p-1 rounded-lg bg-brand-void border border-white/10">
                                        {opts.map((o) => {
                                            const on = (c.value || []).includes(o);
                                            return (
                                                <button
                                                    key={o}
                                                    type="button"
                                                    onClick={() => {
                                                        const arr = c.value || [];
                                                        setCondition(i, { value: on ? arr.filter((v) => v !== o) : [...arr, o] });
                                                    }}
                                                    className={`text-[11px] px-2 py-0.5 rounded-full border ${on ? "bg-brand-indigo text-white border-brand-indigo" : "bg-transparent text-zinc-400 border-white/10"}`}
                                                >
                                                    {o}
                                                </button>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <input
                                        type="text"
                                        value={c.value || ""}
                                        onChange={(e) => setCondition(i, { value: e.target.value })}
                                        data-testid={`segment-cond-value-${i}`}
                                        className="w-full px-3 py-2 rounded-lg bg-brand-void border border-white/10 text-sm outline-none focus:border-brand-indigo"
                                    />
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={() => removeCondition(i)}
                                className="col-span-1 text-zinc-500 hover:text-rose-300"
                            >
                                <X size={16} />
                            </button>
                        </div>
                    );
                })}
            </div>

            <button
                type="button"
                onClick={addCondition}
                data-testid="segment-add-condition"
                className="mt-3 text-sm text-brand-cyan hover:text-white inline-flex items-center gap-1"
            >
                <Plus size={13} /> {t.find.segments.addCondition}
            </button>

            <div className="mt-6 flex justify-end gap-2">
                <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 rounded-lg text-sm text-zinc-300 hover:bg-white/5"
                >
                    {t.find.segments.cancel}
                </button>
                <button
                    type="button"
                    onClick={save}
                    disabled={saving}
                    data-testid="segment-save"
                    className="btn-cta text-white font-semibold text-sm px-5 py-2 rounded-lg disabled:opacity-60"
                >
                    {saving ? "…" : t.find.segments.save}
                </button>
            </div>
        </motion.div>
    );
}
